/**
 * tileModel (#2388, #2489) — the pure view-model for one L3 price tile.
 *
 * Collapses an item + its series into the four tile states the grid renders:
 *   - 'up'    — aggregated price rose year-over-year (tinted green)
 *   - 'down'  — aggregated price fell year-over-year (tinted red)
 *   - 'flat'  — has data but a ~0 % YoY move, or no comparable value a year ago
 *   - 'empty' — no observed data at all ("Keine Daten", disabled/not clickable)
 *
 * #2489: the mini-chart + % change are driven by the "Alle zusammen" aggregate
 * (the per-day volume-weighted combined series the detail view renders), NOT a single
 * most-observed line. The mini is that combined series windowed and drawn by
 * ``sparkline.buildDatedSparkline``, and the % is its year-over-year change —
 * latest price vs the price ~one year earlier — so an item without a comparable
 * value a year ago reads "--" with a flat trend.
 *
 * **HOF-93: the mini is no longer a faithful small copy of the detail "1J"
 * curve, and that correspondence was traded away on purpose.** #2489 gave the
 * tile the detail chart's own 365-day window and preserved every gap, which is
 * what made the Beerenobst tiles draw long straight diagonals across months
 * where nothing was sold. The tile now takes {@link SPARKLINE_TILE_WINDOW_DAYS}
 * (120 d) and breaks its line at gaps. The icon is a glanceable trend
 * indicator; the faithful copy is the chart, one click away. The trade is
 * argued in full in ``sparkline.ts``'s module docstring — the YoY % below is
 * NOT affected, because it reads ``combined``, not what the mini draws.
 *
 * The headline price + as-of date still come from the item's primary
 * (most-observed) series, per #2388/#2441. ``sourceCount`` counts distinct
 * counterparties across the item's data-bearing series (#2539).
 */
import type { PriceBasis, PriceUnit, TrackedItem } from './catalog';
import type { PriceSeries } from './types';
import { toEpochDay } from './priceAt';
import { aggregateObservations, catalogUnitKg, groupKey, type BucketAgg } from './aggregate';
import { defaultSelection, selectVisibleSeries } from './axes';
import { resolvePublishedUnit } from './publishedUnit';
import {
  buildDatedSparkline,
  buildSparkline,
  SPARKLINE_TILE_WINDOW_DAYS,
  type SparklineModel,
  type Trend,
} from './sparkline';

export type TileState = Trend | 'empty';

/** YoY baseline anchor: the aggregated price one year (365 d) before the latest day. */
const YOY_WINDOW_DAYS = 365;
/** Accept a baseline at most this many days BEFORE the 1-year mark (codex 5.5 P2 tolerance). */
const YOY_LOOKBACK_DAYS = 31;
/** |YoY %| at or under this reads as flat (mirrors the sparkline's 0.1 % flat band). */
const YOY_FLAT_PCT = 0.1;

export interface TileModel {
  itemId: string;
  name: string;
  /**
   * The unit this tile's DATA was published under (#2944), or ``null`` when no
   * single unit can honestly be stated.
   *
   * On an EMPTY tile this is the catalog's ``item.unit``: there are no facts to
   * mislabel, and the catalog unit is the right default for whatever gets
   * written next. On a tile WITH data it comes from the facts — see
   * ``publishedUnit.resolvePublishedUnit`` for why the catalog is not consulted
   * there and why a disagreement suppresses the label instead of resolving it.
   */
  unit: PriceUnit | null;
  /**
   * This tile's live facts carry more than one unit (#2944). ``unit`` is null
   * and the view should mark the tile rather than print a winner.
   */
  unitMixed: boolean;
  /** #2441 — the item's price basis (gewicht|einheit|pflanze) for the Saatgut/Pflanzgut split, or undefined. */
  basis?: PriceBasis;
  state: TileState;
  /** The primary series' headline price ({@link headlinePrice}) — its last value, or on a Pflanzgut tile the season's purchases weighted by quantity — or null. */
  lastPrice: number | null;
  /** ISO date of that last observation, or null. */
  asOf: string | null;
  /** Year-over-year change of the aggregated series, in %, or null ("--" when no year-ago value). */
  changePct: number | null;
  sparkline: SparklineModel;
  /** Number of distinct COUNTERPARTIES with data (a multi-source item has ≥ 2) — #2539. */
  sourceCount: number;
  /**
   * The price on this tile came from the curated price book, not from a trade
   * (#2777) — render it with the ``geschätzt`` chip.
   *
   * True only when the item has NO booked series with data. Booked always wins
   * the tile, and where it does the estimate contributes nothing at all: not to
   * the price, not to the sparkline, not to the YoY %, not to ``sourceCount``.
   * A blended figure is the one thing this flag cannot honestly mark, since half
   * of it would be booked — so the model never produces one.
   */
  estimated: boolean;
}

const withData = (series: PriceSeries[]): PriceSeries[] => series.filter((s) => s.points.length > 0);

/**
 * The representative series for the tile headline among an item's own-price
 * series: most points, then latest observation, then the series' own id.
 *
 * **The third term is `seriesId`, and it is deliberately locale-free** (HOF-280).
 * It used to be `a.counterparty.localeCompare(b.counterparty, 'de')`, which UDB —
 * which materialises this same choice into the `preisspiegel_tile` fact and has no
 * ICU — could only match with a hand-written re-implementation of German collation:
 * 231 lines and four measured tables, three of whose four codex rounds were fixing
 * the previous round's fix.
 *
 * Byte-exact collation would still not have given parity, because the two
 * comparators did not have the same arity. This one has three terms and returned
 * `localeCompare`'s result as its last word; UDB's had four. Where ICU returns `0`
 * — U+00AD SOFT HYPHEN and U+200B ZERO WIDTH SPACE are fully ignorable to it —
 * `Array#sort`'s stability handed the answer to PAYLOAD order here while UDB fell
 * back to the raw name in code-point order. Two different rules on identical input,
 * and no table entry can reconcile them.
 *
 * `seriesId` closes both. It is `<itemId>__<side>__<counterparty-slug>[…]` and ASCII
 * by construction (`_slug` folds umlauts, then maps `[^a-z0-9]+` to `-`), so a plain
 * lexicographic compare is identical in both languages with no collation and no
 * normalisation; and it is unique per series — `cells.parseCell` refuses a cell
 * without one — so it is a total order on its own and this function never returns
 * `0`. Parity stopped being something a 167-name fixture measures and became
 * something the comparison's shape guarantees.
 *
 * The tie-break is reached only when two series tie on point count AND latest date.
 * The drill-in chart's own series ORDERING is `axes.ts` and still German-collated:
 * that is a legend a farmer reads, it reaches no number, and it is out of scope here.
 */
export function primarySeries(series: PriceSeries[]): PriceSeries | null {
  const live = withData(series);
  if (live.length === 0) return null;
  return [...live].sort((a, b) => {
    if (b.points.length !== a.points.length) return b.points.length - a.points.length;
    const la = toEpochDay(a.points[a.points.length - 1].date);
    const lb = toEpochDay(b.points[b.points.length - 1].date);
    if (lb !== la) return lb - la;
    return a.seriesId < b.seriesId ? -1 : a.seriesId > b.seriesId ? 1 : 0;
  })[0];
}

/**
 * The tile's headline price off its primary series (2026-10).
 *
 * The last point — except on a Pflanzgut tile (``basis: 'pflanze'``), where a planting is
 * bought in several deliveries and the last is often a small top-up: there it is every
 * point in the last point's calendar year, weighted by its traded quantity
 * (``amount / value``). WF's 2026 Lagorai is 2.430 pots at 4,40 €/Stk and a 300-pot top-up
 * at 2,10; the season reads 4,15, the last point read 2,10. A point that cannot state a
 * quantity leaves nothing honest to weight by, and the last point answers as before.
 *
 * MIRRORED term for term by UDB's ``_headline_price`` (the materialised summary), held equal
 * by the ``preisspiegel_tile_parity`` vector — change both in one PR.
 */
export function headlinePrice(points: PriceSeries['points'], seasonWeighted: boolean): number {
  const last = points[points.length - 1];
  if (!seasonWeighted) return last.value;
  const year = last.date.slice(0, 4);
  let wNum = 0;
  let wDen = 0;
  for (const point of points) {
    if (point.date.slice(0, 4) !== year) continue;
    const weight = catalogUnitKg(point.provenance, point.value);
    if (weight == null || !(weight > 0)) return last.value;
    wNum += point.value * weight;
    wDen += weight;
  }
  return wDen > 0 ? wNum / wDen : last.value;
}

/**
 * The aggregated "Alle zusammen" per-day series behind the tile — the
 * same combined series the detail view renders (#2457), over the item's DEFAULT
 * side + quality (channels aggregated together, matching the detail's "alle"
 * view), ascending by date. Empty when the item has no data.
 */
function combinedSeries(series: PriceSeries[]): BucketAgg[] {
  const sel = defaultSelection(series);
  if (!sel || sel.side == null) return [];
  const visible = selectVisibleSeries(series, {
    side: sel.side,
    ...(sel.quality != null ? { quality: sel.quality } : {}),
  });
  // DAY grain, unconditionally: a tile's sparkline is a shape at a glance, and
  // the chart's `?g=` is a choice the reader makes AFTER drilling in — reading
  // it here would make a grid of tiles change shape from a control that is not
  // on screen. The side is pinned to the item's default so the tile still
  // quotes ONE number; the chart, which has a legend, draws both.
  return aggregateObservations(visible, 'day').byGroup[groupKey(sel.side, 'all')] ?? [];
}

/**
 * Year-over-year change of the combined series, in %: the latest price vs the
 * nearest price on/before ``latest − 365 d`` (within a ±31-day lookback, codex
 * 5.5 P2). Returns null ("--") when no such baseline exists in that window, or
 * when the baseline is zero (relative change undefined).
 */
export function yoyChangePct(combined: BucketAgg[]): number | null {
  if (combined.length === 0) return null;
  const last = combined[combined.length - 1];
  const targetE = toEpochDay(last.date) - YOY_WINDOW_DAYS;
  const floorE = targetE - YOY_LOOKBACK_DAYS;

  // Combined is ascending; the last day within [floorE, targetE] is the nearest-before baseline.
  let baseline: BucketAgg | null = null;
  for (const d of combined) {
    const e = toEpochDay(d.date);
    if (e > targetE) break;
    if (e >= floorE) baseline = d;
  }
  if (!baseline || baseline.price === 0) return null;
  return ((last.price - baseline.price) / Math.abs(baseline.price)) * 100;
}

/**
 * Trend classification for a YoY %: flat when within the band or when there is no baseline ("--").
 *
 * Exported for ``tileFromSummary`` (HOF-270), which builds the same model from a
 * materialised summary. The classification stays HERE and is shared rather than
 * mirrored server-side: it is one comparison against a band, it is already tested,
 * and every field the summary duplicates is a field the parity fixture must police.
 */
export function trendFromPct(changePct: number | null): Trend {
  if (changePct == null) return 'flat';
  if (changePct > YOY_FLAT_PCT) return 'up';
  if (changePct < -YOY_FLAT_PCT) return 'down';
  return 'flat';
}

/**
 * Build the tile view-model for an item from its series.
 *
 * ── #2777: two populations, one tile, and booked always wins ─────────────────
 *
 * The series may now include the curated price book (``s.estimate``). The tile
 * resolves them in strict order rather than merging:
 *
 *  1. **Any booked series with data → the tile is booked**, built from those
 *    alone. Byte-identical to pre-#2777 for such an item, and that is the
 *    load-bearing part: a reference price sitting beside real trades must not
 *    move the headline, the sparkline, the YoY % or the Quellen badge by so much
 *    as a decimal. Blending is how a curated number ends up inside a figure the
 *    farmer reads as "what I was paid" (#2695).
 *  2. **Otherwise, the book answers** — with ``estimated: true`` so the view can
 *    mark it. An untraded item with a reference price is not an empty tile; that
 *    reading is what emptied the seven #2694 Saatgut tiles.
 *  3. **Neither → empty**, exactly as before.
 *
 * The estimate branch states no trend, and cannot: the book is one pinned anchor
 * date (#2538), so there is no year-ago value and no curve. It renders as "--"
 * with a flat chip and no sparkline — which is the honest shape of "we know one
 * number about this item".
 */
export function buildTileModel(
  item: TrackedItem,
  series: PriceSeries[],
  indexUnits: readonly (string | null | undefined)[] = [],
): TileModel {
  const booked = series.filter((s) => !s.estimate);
  const estimates = series.filter((s) => s.estimate);
  const estimated = withData(booked).length === 0 && withData(estimates).length > 0;
  // The population the tile speaks for. Never the union: whichever answers, the
  // other contributes nothing.
  const source = estimated ? estimates : booked;
  const primary = primarySeries(source);

  if (!primary) {
    return {
      itemId: item.id,
      name: item.name,
      // #2944 — the ONE place the catalog unit is still the answer: an empty
      // tile has no published fact to contradict, and the catalog unit is what
      // the next write will carry.
      unit: item.unit,
      unitMixed: false,
      ...(item.basis ? { basis: item.basis } : {}),
      state: 'empty',
      lastPrice: null,
      asOf: null,
      changePct: null,
      sparkline: buildSparkline([]),
      sourceCount: 0,
      estimated: false,
    };
  }

  // #2489 — mini-chart + % come from the aggregated "Alle zusammen" series...
  //
  // #2777 — and ONLY from booked observations. `aggregateObservations` is the
  // price-averaging machinery; an estimate entering here would become a trading
  // day of its own, on a date nothing was sold. On an estimate tile there is
  // therefore nothing to aggregate at all, which is why the branch is empty
  // rather than pointed at `source`.
  const combined = estimated ? [] : combinedSeries(booked);
  // HOF-93 — the TILE's window, not the detail chart's. Passed explicitly rather
  // than moved onto `buildDatedSparkline`'s default, so the 365-day behaviour
  // stays exactly where every other caller expects to find it. Gap-breaking is
  // that function's default and needs no argument here.
  const sparkline = buildDatedSparkline(
    combined.map((d) => ({ date: d.date, value: d.price })),
    { windowDays: SPARKLINE_TILE_WINDOW_DAYS },
  );
  const changePct = estimated ? null : yoyChangePct(combined);
  // ...while price + as-of stay on the primary series (unchanged).
  const last = primary.points[primary.points.length - 1];
  // #2944 — labelled by what the facts SAY, over the same population the tile
  // speaks for (``source``, so a curated reference price cannot blank a traded
  // tile's headline).
  const published = resolvePublishedUnit(source, indexUnits);

  return {
    itemId: item.id,
    name: item.name,
    unit: published.unit,
    unitMixed: published.mixed,
    ...(item.basis ? { basis: item.basis } : {}),
    state: trendFromPct(changePct),
    lastPrice: headlinePrice(primary.points, item.basis === 'pflanze'),
    asOf: last.date,
    changePct,
    sparkline,
    // #2539 — distinct counterparties, NOT data-bearing series: series_id is
    // <item>__<side>__<cp-slug>[__quality][__channel], so one buyer trading two
    // qualities or two channels used to inflate the badge to "2 Quellen".
    sourceCount: new Set(withData(source).map((s) => s.counterparty)).size,
    estimated,
  };
}
